import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaUser, FaPhone, FaGraduationCap, FaCertificate, FaDownload, FaEnvelope, FaIdCard, FaBuilding, FaBook, FaChartBar } from 'react-icons/fa';
import { toast } from 'sonner';
import { adminApi, getAdminUser, getToken, clearAdminSession } from '@/lib/adminAuth';
import { AdminAccountDialog } from '@/components/AdminAccountDialog';

const FILIERE_CODES = {
  "Sciences Économiques": "ECO",
  "Droit": "DRT",
  "Marketing et Management": "MKT",
  "Anglais": "ANG",
  "Mathématiques": "MAT",
  "Lettres Modernes": "LET",
  "Sciences Informatiques": "INF",
  "Histoire": "HIS",
  "Géographie": "GEO",
  "GRH": "GRH",
  "Sciences Physiques": "PHY"
};

export default function AdminPreinscriptions() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState('');

  const [preinscriptions, setPreinscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, parFiliere: {}, parCampus: {} });
  const [showCodification, setShowCodification] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    setCurrentUser(getAdminUser() || '');
    fetchPreinscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin/login');
  };

  const fetchPreinscriptions = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/pre-registrations');
      const data = response.data;
      setPreinscriptions(data);

      // Calculate stats
      const parFiliere = {};
      const parCampus = {};
      data.forEach(p => {
        parFiliere[p.filiere] = (parFiliere[p.filiere] || 0) + 1;
        if (p.campus) {
          parCampus[p.campus] = (parCampus[p.campus] || 0) + 1;
        }
      });
      setStats({ total: data.length, parFiliere, parCampus });
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status !== 401) {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Matricule', 'Nom', 'Prénom', 'Email', 'WhatsApp', 'Campus', 'Filière', 'Dernier Diplôme', 'Diplôme Sollicité', 'Date'];
    const rows = preinscriptions.map(p => [
      p.matricule || 'N/A',
      p.nom,
      p.prenom,
      p.email || '',
      p.whatsapp,
      p.campus || p.commune || '',
      p.filiere,
      p.dernier_diplome || p.diplome || '',
      p.diplome_sollicite || '',
      new Date(p.created_at).toLocaleString('fr-FR')
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `preinscriptions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Login Form
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-secondary mb-2">
              Administration - Préinscriptions
            </h1>
            <p className="text-gray-600">Connecté en tant que : <strong>{currentUser}</strong></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <AdminAccountDialog />
            <Button
              onClick={() => navigate('/admin/statistics')}
              className="bg-brand-secondary hover:bg-brand-secondary/90 text-white"
            >
              <FaChartBar className="mr-2" />
              Statistiques & Messages
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              variant="outline"
              className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
            >
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-brand-primary to-brand-primary-dark text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Inscrits</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <FaUser className="text-4xl text-white/30" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-brand-secondary to-blue-900 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Filières actives</p>
                <p className="text-3xl font-bold">{Object.keys(stats.parFiliere).length}</p>
              </div>
              <FaGraduationCap className="text-4xl text-white/30" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-600 to-green-700 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Campus actifs</p>
                <p className="text-3xl font-bold">{Object.keys(stats.parCampus).length}</p>
              </div>
              <FaBuilding className="text-4xl text-white/30" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-600 to-purple-700 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Actions</p>
                <button
                  onClick={exportToCSV}
                  className="mt-2 flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-md transition-colors text-sm"
                >
                  <FaDownload />
                  Exporter CSV
                </button>
              </div>
            </div>
          </Card>
        </div>

        {/* Codification Table Toggle */}
        <Card className="p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-brand-secondary flex items-center gap-2">
              <FaBook className="text-brand-primary" />
              Table de Codification des Filières
            </h2>
            <Button
              onClick={() => setShowCodification(!showCodification)}
              variant="outline"
              size="sm"
            >
              {showCodification ? 'Masquer' : 'Afficher'}
            </Button>
          </div>
          
          {showCodification && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(FILIERE_CODES).map(([filiere, code]) => (
                <div key={code} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                  <span className="text-sm text-gray-700">{filiere}</span>
                  <span className="bg-brand-primary text-white px-3 py-1 rounded font-mono font-bold text-sm">
                    {code}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Format du Matricule :</strong> UCI + Année (2 chiffres) + NOM (3 lettres) + CODE FILIÈRE + N° d'ordre
              <br />
              <strong>Exemple :</strong> UCI26KOU-GRH-001 = Étudiant KOUASSI en GRH, 1er inscrit en 2026
            </p>
          </div>
        </Card>

        {/* Stats par Filière */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h2 className="text-xl font-bold text-brand-secondary mb-4">Par Filière</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {Object.entries(stats.parFiliere)
                .sort((a, b) => b[1] - a[1])
                .map(([filiere, count]) => (
                  <div key={filiere} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">{filiere}</span>
                    <span className="bg-brand-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-bold text-brand-secondary mb-4">Par Campus</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {Object.entries(stats.parCampus)
                .sort((a, b) => b[1] - a[1])
                .map(([campus, count]) => (
                  <div key={campus} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">{campus}</span>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {count}
                    </span>
                  </div>
                ))}
              {Object.keys(stats.parCampus).length === 0 && (
                <p className="text-gray-500 text-center py-4">Aucune donnée de campus</p>
              )}
            </div>
          </Card>
        </div>

        {/* Liste des préinscriptions */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-brand-secondary mb-6">
            Liste des Préinscriptions ({preinscriptions.length})
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left p-3 font-semibold text-gray-700">Matricule</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Nom & Prénom</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left p-3 font-semibold text-gray-700">WhatsApp</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Campus</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Filière</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Diplôme Sollicité</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody>
                {preinscriptions.map((p) => (
                  <tr 
                    key={p.id} 
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaIdCard className="text-brand-primary" />
                        <span className="font-mono font-medium text-brand-secondary">
                          {p.matricule || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaUser className="text-gray-400" />
                        <span className="font-medium text-gray-800">
                          {p.nom} {p.prenom}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaEnvelope className="text-blue-600" />
                        <a 
                          href={`mailto:${p.email}`}
                          className="text-brand-primary hover:underline text-sm"
                        >
                          {p.email || '-'}
                        </a>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaPhone className="text-green-600" />
                        <a 
                          href={`https://wa.me/${p.whatsapp?.replace(/\s+/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-primary hover:underline"
                        >
                          {p.whatsapp}
                        </a>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaBuilding className="text-orange-500" />
                        <span className="text-sm text-gray-700">{p.campus || p.commune || '-'}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaGraduationCap className="text-brand-secondary" />
                        <span className="text-sm text-gray-700">{p.filiere}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <FaCertificate className="text-yellow-600" />
                        <span className="text-sm font-medium text-gray-700">
                          {p.diplome_sollicite || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {new Date(p.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preinscriptions.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucune préinscription pour le moment</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
